import { useEffect, useState } from 'react';
import { formatCurrency, formatDateTime } from '../lib/mockData';
import {
  FileText,
  Clock,
  Copy,
  Check,
  ExternalLink,
  Receipt,
  TrendingUp,
  Filter,
  Wallet,
  Info,
  List,
  X,
  CheckCircle2
} from 'lucide-react';
import SplitDetailsModal from '../components/SplitDetailsModal';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { AppLayout } from '../components/AppLayout';
import { cn } from '../service/CoreFunctions';
import { Button } from '../components/ui/button';
import { useLoading } from '../hooks/useLoading';
import { send } from '../components';
import { InvoiceStatus } from '../consts/invoiceStatus';

const MerchantDashboard = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState('all');
  const [copiedId, setCopiedId] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mockInvoices, setMockInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [stats, setStats] = useState({});
  const { showLoading } = useLoading();

  const getList = async () => {
    const res = await send("cu0901", {}, showLoading);

    if (res.status === 'error') {
      toast.error(res.message);
      return;
    }
    setMockInvoices(res);
  };

  const confirmCancelInvoice = (invoice, onConfirm) => {
    toast(
      t('confirmCancel') || 'Нэхэмжлэхийг цуцлах уу?',
      {
        description: invoice?.txndesc
          ? `${invoice.txndesc} • ${formatCurrency(invoice.amount_total, i18n.language, user.currency)}`
          : undefined,

        action: {
          label: t('cancelInvoice') || 'Цуцлах',
          onClick: onConfirm,
        },

        cancel: {
          label: t('close') || 'Болих',
          onClick: () => { },
        },

        duration: 8000,
        position: 'top-right'
      }
    );
  };

  const sendManualFinish = (invoice) => {
    toast(
      t('confirmFinish') || 'Үлдэгдлийг бэлнээр төлөгдсөн гэж үзээд дуусгах уу?',
      {
        action: {
          label: t('finish') || 'Дуусгах',
          onClick: async () => {
            const res = await send("cu0906", { invid: invoice.invid }, showLoading);
            if (res.status === 'error') return toast.error(res.message);
            toast.success(t('finished') || 'Дуусгалаа');
            getList();
          }
        },
        cancel: { label: t('close') || 'Болих' },
        duration: 8000,
        position: 'top-right'
      }
    );
  }

  const sendCancel = async (invoice) => {
    const canCancel = Number(invoice.can_cancel) === 1;
    const cancelReasonText = getCancelReasonText(invoice);

    if (!canCancel) {
      toast.error(cancelReasonText);
      return;
    }
    confirmCancelInvoice(invoice, async () => {
      const res = await send("cu0905", { invid: invoice.invid }, showLoading);
      if (res.status === 'error') {
        toast.error(res.message);
        return;
      }

      toast.success(t('canceled') || 'Цуцаллаа');
      getList();
    })
  }

  useEffect(() => {
    getList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  useEffect(() => {
    let total = 0;
    let paid = 0;
    let pending = 0;

    let totalInvoiceAmount = 0;   // 🔥 Нийт нэхэмжлэх дүн
    let totalPaidAmount = 0;      // 🔥 Нийт төлсөн дүн (status үл хамаарна)
    let totalPendingAmount = 0;
    let totalSettleAmount = 0;
    let totalFeeAmount = 0;

    const normalizeStatus = (s) => (s ?? '').toString().toLowerCase();

    const filtered = mockInvoices.filter((invoice) => {
      if (filter === InvoiceStatus.ALL) return true;
      return normalizeStatus(invoice.status) === filter;
    });

    mockInvoices.forEach(i => {
      total++;

      const status = normalizeStatus(i.status);
      const amountTotal = Number(i.amount_total) || 0;
      const paidAmount = Number(i.total_paid_amount) || 0;

      totalInvoiceAmount += amountTotal;
      totalPaidAmount += paidAmount;

      if (status === InvoiceStatus.PAID) paid++;

      if (status === InvoiceStatus.PENDING) {
        // pending үлдэгдэл = нийт - төлсөн
        const remaining = Math.max(0, amountTotal - paidAmount);
        totalPendingAmount += remaining;

        // хүсвэл зөвхөн үлдэгдэлтэй pending-г тоол
        if (remaining > 0) pending++;
      }

      totalSettleAmount += Number(i.total_settle_amount) || 0;
      totalFeeAmount += Number(i.total_fee_amount) || 0;
    });

    const paymentRate =
      totalInvoiceAmount > 0
        ? Math.min(100, Math.max(0, (totalPaidAmount / totalInvoiceAmount) * 100))
        : 0;

    setFilteredInvoices(filtered);

    setStats({
      total,
      paid,
      pending,
      totalInvoiceAmount,
      totalPaidAmount,
      totalPendingAmount,
      totalSettleAmount,
      totalFeeAmount,
      paymentRate,
    });
  }, [mockInvoices, filter]);

  const handleCopyLink = (invoice) => {
    const link = `${invoice.payment_link}`;
    navigator.clipboard.writeText(link);
    setCopiedId(invoice.id);
    toast.success(t('copied'));
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleViewDetails = async (invoice) => {
    const res = await send("cu0904", { invid: invoice.invid }, showLoading);
    if (res.status === 'error') {
      toast.error(res.message);
      return;
    }
    setSelectedInvoice(res);
    setIsModalOpen(true);
  };

  const getCancelReasonText = (inv) => {
    switch (inv?.cancel_block_reason) {
      case 'PAID_EXISTS':
        return t('cancelBlockedPaid') || 'Төлөлт хийгдсэн тул цуцлах боломжгүй.';
      case 'LOCK_25_MIN':
        return t('cancelBlocked25Min') || 'Сүүлийн төлөлтийн хүсэлтээс хойш 25 минут болоогүй тул цуцлах боломжгүй.';
      case 'ALREADY_CANCELED':
        return t('alreadyCanceled') || 'Аль хэдийн цуцлагдсан.';
      case 'INVOICE_EXPIRED':
        return t('invoiceExpired') || 'Нэхэмжлэхийн хугацаа дууссан.';
      case 'INVOICE_PAID':
        return t('invoicePaid') || 'Нэхэмжлэх төлөгдсөн.';
      default:
        return t('cancelNotAllowed') || 'Цуцлах боломжгүй.';
    }
  };

  return (
    <AppLayout>
      <div className="space-y-8 animate-fade-up">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              {t('merchantDashboard')}
            </h1>
            <p className="mt-1 text-muted-foreground">
              {t('welcomeBackMerchant')}, {user?.businessName}
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <TooltipProvider>
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            <div className="card-elevated p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('totalInvoices')}</p>
                  <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                </div>
              </div>
            </div>


            <div className="card-elevated p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-warning/10 rounded-xl flex items-center justify-center">
                  <Clock className="h-6 w-6 text-warning" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('totalPending')}</p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(stats.totalPendingAmount, i18n.language, user.currency)}
                  </p>
                </div>
              </div>
            </div>

            <div className="card-elevated p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-accent-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('conversionRate')}</p>
                  <p className="text-2xl font-bold text-foreground">
                    {Math.round(stats.paymentRate ?? 0)}%
                  </p>
                </div>
              </div>
            </div>

          </div>

          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-2">
            <div className="card-elevated p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-success/10 rounded-xl flex items-center justify-center">
                  <span className="text-success font-bold text-xl">₮</span>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('totalPaid')}</p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(stats.totalPaidAmount, i18n.language, user.currency)}
                  </p>
                </div>
              </div>
            </div>
            <div className="card-elevated p-6 relative group">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-warning/10 rounded-xl flex items-center justify-center">
                  <Wallet className="h-6 w-6 text-warning" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-1">
                    <p className="text-sm text-muted-foreground">{t('pendingSettlement')}</p>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3.5 w-3.5 text-muted-foreground/50 hover:text-warning cursor-help transition-colors" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[200px] text-xs">
                        {t('pendingSettlementHelp')}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency((stats.totalPaidAmount ?? 0) - (stats.totalSettleAmount ?? 0) - (stats.totalFeeAmount ?? 0), i18n.language, user.currency)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </TooltipProvider>

        {/* Recent Payments */}
        <div className="card-elevated">
          <div className="p-6 border-b border-border">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-lg font-semibold">{t('recentPayments')}</h2>

              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <div className="flex bg-muted rounded-lg p-1">
                  {([InvoiceStatus.ALL, InvoiceStatus.PAID, InvoiceStatus.PENDING]).map((status) => (
                    <button
                      key={status}
                      onClick={() => setFilter(status)}
                      className={cn(
                        'px-3 py-1.5 text-sm font-medium rounded-md transition-all',
                        filter === status
                          ? 'bg-card text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {status === InvoiceStatus.ALL ? t('all') : status === InvoiceStatus.PAID ? t('paid') : t('pending')}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="divide-y divide-border">
            {filteredInvoices.map((invoice, index) => {
              const canCancel = Number(invoice.can_cancel) === 1;
              const cancelReasonText = getCancelReasonText(invoice);

              return (
                <div
                  key={invoice.id}
                  className="group p-4 sm:p-6 hover:bg-muted/40 transition-all duration-200 animate-fade-up border-l-4 border-transparent hover:border-primary/50"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">

                    {/* Icon Area */}
                    <div className="hidden sm:flex flex-shrink-0">
                      <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center transition-colors shadow-sm",
                        invoice.status === InvoiceStatus.PAID ? "bg-success/10 text-success" : "bg-warning/10 text-warning"
                      )}>
                        <Receipt className="h-5 w-5" />
                      </div>
                    </div>

                    {/* Main Info */}
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-foreground truncate text-base">
                          {invoice.txndesc}
                        </h3>
                        <span className={cn(
                          'badge-status shadow-sm',
                          invoice.status === InvoiceStatus.PAID ? 'badge-success' : 'badge-warning'
                        )}>
                          {invoice.status === InvoiceStatus.PAID ? t('paid') : t('pending')}
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1.5 bg-muted/60 px-2 py-0.5 rounded-md border border-border/50">
                          <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">ID</span>
                          <span className="font-mono font-medium text-foreground/80 text-xs">{invoice?.id ?? ''}</span>
                        </div>

                        {invoice.merchant_ref && (
                          <div className="flex items-center gap-1.5">
                            <span className="w-1 h-1 rounded-full bg-border" />
                            <span className="font-mono text-xs">{invoice.merchant_ref}</span>
                          </div>
                        )}

                        <div className="flex items-center gap-1.5">
                          <span className="w-1 h-1 rounded-full bg-border" />
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3 h-3 opacity-50" />
                            <span className="text-xs">{formatDateTime(invoice.created_at, i18n.language)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Amount & Actions */}
                    <div className="flex items-center justify-between sm:justify-end gap-6 sm:pl-6 sm:border-l border-border/40">
                      <div className="text-right">
                        <p className={cn(
                          "text-lg font-bold tabular-nums tracking-tight",
                          invoice.status === InvoiceStatus.PAID ? "text-success" : "text-foreground"
                        )}>
                          {formatCurrency(invoice.amount_total, i18n.language, user.currency)}
                        </p>
                        <p className="text-xs text-muted-foreground hidden sm:block">
                          {t('total')}
                        </p>
                      </div>

                      <div className="flex items-center gap-1 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-all duration-200">
                        {(Number(invoice.count_total) || 1) > 1 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(invoice);
                            }}
                            className="h-9 w-9 hover:bg-background hover:shadow-sm"
                            title={t('viewDetails')}
                          >
                            <List className="h-4 w-4 text-primary" />
                          </Button>
                        )}
                        {/* ✅ Cancel */}
                        {invoice.status === InvoiceStatus.PENDING && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={!canCancel}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    sendCancel(invoice)
                                  }}
                                  className="h-9 w-9 hover:bg-background hover:shadow-sm"
                                  title={t('cancelInvoice') || 'Цуцлах'}
                                >
                                  <X className="h-4 w-4 text-destructive" />
                                </Button>
                              </span>
                            </TooltipTrigger>

                            {!canCancel && (
                              <TooltipContent className="max-w-[240px] text-xs">
                                {cancelReasonText}
                              </TooltipContent>
                            )}
                          </Tooltip>
                        )}
                        {/* Гараар дуусгах */}
                        {(invoice.status === InvoiceStatus.PENDING && !canCancel &&
                          invoice?.cancel_block_reason !== 'LOCK_25_MIN') &&
                          (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      sendManualFinish(invoice)
                                    }}
                                    className="h-9 w-9 hover:bg-background hover:shadow-sm"
                                    title={t('finish') || 'Дуусгах'}
                                  >
                                    <CheckCircle2 className="h-4 w-4 text-success" />
                                  </Button>
                                </span>
                              </TooltipTrigger>
                            </Tooltip>
                          )}
                        {/* Copy */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyLink(invoice);
                          }}
                          className="h-9 w-9 hover:bg-background hover:shadow-sm"
                          title={t('copyLink')}
                        >
                          {copiedId === invoice.id ? (
                            <Check className="h-4 w-4 text-success" />
                          ) : (
                            <Copy className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          asChild
                          className="h-9 w-9 hover:bg-background hover:shadow-sm"
                          title={t('openLink')}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <a href={invoice.payment_link} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4 text-muted-foreground" />
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredInvoices.length === 0 && (
              <div className="p-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">{t('noInvoicesFound')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <SplitDetailsModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        invoice={selectedInvoice}
      />
    </AppLayout>
  );
};

export default MerchantDashboard;
